/**
 * useOrderLiveUpdates — ONE live feed for an order-details page
 * ─────────────────────────────────────────────────────────────────────────────
 * WHY THIS EXISTS (19 Sep 2026). A 3PL rider took a restaurant order through
 * Picked up, On the way and Delivered and the Lusuku staff member watching
 * /admin/restaurantOrderDetails saw "PICKED UP" until they reloaded. That
 * page opened a socket only to list riders; it listened for no order event at
 * all. The vendor pages listened, but each with its own copy of the handlers,
 * and every one of them emitted `joinOrderRoom`, which no server handler
 * answered, so the `orderUpdated` full-document feed never reached any of
 * them. And the copies that did react replaced the whole order in the store
 * with `{ _id, delivery_status }`, blanking the items and the customer.
 *
 * This hook is the one implementation:
 *   • its own authenticated socket (the token is read at connect time via
 *     socketAuthOptions, never captured at module load);
 *   • `joinOrderRoom` on every connect, which the server now gates and
 *     answers with the `orderWatch:<orderId>` room;
 *   • listens for every event any rider path emits about an order
 *     (`orderUpdated` full doc from the change stream, the global
 *     `orderStatusChanged` / `order:status_changed`, `orderStatusUpdate`),
 *     filtered to THIS order;
 *   • on a hit, re-reads GET /weborders/:id (populated, the source of truth)
 *     and puts it in the `orderDetails` slice WITHOUT the loading flag, so the
 *     page updates in place instead of flashing a spinner, then toasts and
 *     calls `onChange`. Refetches are coalesced, so a burst of events (the
 *     rider's pickup writes three times) costs one request.
 *
 * Pages keep their own rider-list socket; this hook only carries order news.
 *
 * @param {string} orderId
 * @param {object} [opts]
 * @param {() => Promise<object|void>} [opts.refetch]  silent re-read for pages
 *        whose order lives in another slice (retailer/wholesaler, vendor card);
 *        default: refreshOrderDetails into `orderDetails`. Must NOT flip a
 *        loading flag. Should resolve to the fresh order.
 * @param {(order: object, event: {name: string, status?: string}) => void} [opts.onChange]
 * @param {boolean} [opts.toast=true]  toast on a status change
 * @returns {{ live: boolean, refresh: () => Promise<void> }}
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { io } from "socket.io-client";
import { toast } from "react-toastify";
import { refreshOrderDetails } from "../actions/webOrderActions";
import socketAuthOptions from "../utilities/socketAuth";
import { getUserFromLocalStorage } from "../utilities/user.js";

const SOCKET_URL = process.env.REACT_APP_SERVER_URL;

/** Events that mean "this order changed", and where each carries its id. */
const ORDER_EVENTS = {
  orderUpdated: (d) => ({ id: d?._id, status: d?.delivery_status }),
  orderStatusChanged: (d) => ({ id: d?.orderId, status: d?.status }),
  "order:status_changed": (d) => ({ id: d?.orderId, status: d?.status }),
  orderStatusUpdate: (d) => ({ id: d?.orderId, status: d?.status }),
};

const REFETCH_COALESCE_MS = 400;

const prettyStatus = (status) =>
  String(status || "")
    .toLowerCase()
    .replace(/_/g, " ")
    .replace(/^\w/, (c) => c.toUpperCase());

export default function useOrderLiveUpdates(orderId, opts = {}) {
  const { onChange, refetch, toast: showToast = true } = opts;
  const dispatch = useDispatch();
  const [live, setLive] = useState(false);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const refetchRef = useRef(refetch);
  refetchRef.current = refetch;
  const lastStatusRef = useRef(null);
  const timerRef = useRef(null);
  const pendingRef = useRef(null);

  const refresh = useCallback(async () => {
    if (!orderId) return;
    try {
      const data = refetchRef.current
        ? await refetchRef.current()
        : await dispatch(refreshOrderDetails(orderId));
      const event = pendingRef.current || { name: "refresh" };
      pendingRef.current = null;
      const status = data?.delivery_status;
      const previous = lastStatusRef.current;
      if (showToast && status && status !== previous && event.name !== "refresh") {
        const riderDeclined =
          previous === "ASSIGNED" && status === "PROCESSING" && !data?.riderId;
        toast.info(
          riderDeclined
            ? "Rider declined the order. It is back in Processing."
            : `Order status updated to ${prettyStatus(status)}`,
          { toastId: `order-live-${orderId}-${status}` }
        );
      }
      lastStatusRef.current = status || previous;
      onChangeRef.current?.(data, { ...event, previous });
    } catch (err) {
      // The socket signal is a hint; the next event or reload will catch up.
      // eslint-disable-next-line no-console
      console.warn("[useOrderLiveUpdates] refetch failed:", err?.message || err);
    }
  }, [orderId, dispatch, showToast]);

  useEffect(() => {
    if (!orderId || !SOCKET_URL) return undefined;
    const user = getUserFromLocalStorage();
    const socket = io(SOCKET_URL, socketAuthOptions({ userId: user?.result?._id }));

    const schedule = (event) => {
      pendingRef.current = event;
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => {
        timerRef.current = null;
        refresh();
      }, REFETCH_COALESCE_MS);
    };

    const handlers = Object.entries(ORDER_EVENTS).map(([name, pick]) => {
      const handler = (payload) => {
        const { id, status } = pick(payload) || {};
        if (!id || String(id) !== String(orderId)) return;
        schedule({ name, status });
      };
      socket.on(name, handler);
      return [name, handler];
    });

    const onConnect = () => {
      socket.emit("joinOrderRoom", orderId, (ack) => {
        if (ack && ack.ok === false) {
          // eslint-disable-next-line no-console
          console.warn(`[useOrderLiveUpdates] joinOrderRoom refused: ${ack.error}`);
        }
      });
      setLive(true);
      // A reconnect may have missed events; read the truth once.
      schedule({ name: "refresh" });
    };
    const onDisconnect = () => setLive(false);
    socket.on("connect", onConnect);
    socket.on("disconnect", onDisconnect);

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = null;
      handlers.forEach(([name, handler]) => socket.off(name, handler));
      socket.off("connect", onConnect);
      socket.off("disconnect", onDisconnect);
      socket.emit("leaveOrderRoom", orderId);
      socket.disconnect();
    };
  }, [orderId, refresh]);

  return { live, refresh };
}
